import { Time } from '../types/time';
import {
  ReservableTicket,
  ReservablePackage,
  ReservableAddon,
  ReservableEventTemplate,
  ReservableEventTemplateSnapshot,
  BareEventHost,
  RESERVABLE_EXPERIENCE_TYPE,
} from '../types/reservable';
import {
  ReserveTimeline,
  ReserveTimelineEvent,
  ReserveTimelineItem,
  ReserveEvent,
  ReserveEventsByDate,
  TimeOption,
  DateOption,
  TicketSelections,
  BareReserveTimeline,
} from '../types/reserve';
import {
  BareReserveSchedule,
  BareReserveScheduleRule,
  ReserveSchedule,
  ReserveScheduleRuleParts,
  ReserveScheduleSlot,
  ReserveScheduleSlotDayContext,
  ReserveScheduleSlotPriceOverrides,
} from '../types/reserveSchedule';
import { ObjectId } from 'bson';
import { BrandAttribute, BrandLevel, BrandSegment, HourFormat } from '../types/brand';
import { BareEventTemplate, EventTemplate } from '../types/eventTemplate';
import { CartSession, CheckoutProgress, ReservationDraft } from '../types/cartSession';
import { ReserveCacheSlot, ReserveScheduleSlotSnapshot } from '../types/reserveCache';
import { forceIdToString, keyFromLabel, makeFullName, safeIdCompare } from './utils';
import crypto from 'crypto';
import {
  EnhancedReservationStatus,
  FlattenedReservation,
  NeedsAttentionItem,
  NeedsAttentionType,
  Reservation,
  ReservationAnalytics,
  ReservationCustomer,
  ReservationOccurrence,
  ReservationPayment,
  ReservationPaymentSummary,
} from '../types/reservation';
import {
  groupBy,
  GroupedItems,
  MappedItems,
  notUndefined,
  onlyUnique,
  mapBy,
  notFalsy,
} from './functional';
import { rruleFromScheduleRule, rruleCheckDate } from './rrule';
import {
  createIsoDateList,
  dateToIsoUTC,
  timeCompare,
  daysBetweenIsoDates,
  getServerDateLocal,
  doTimeRangesOverlap,
  isoToDateUTC,
  isoToDateLocal,
  getLocaleDateStringLocal,
  timeToString,
  addMinutesToTime,
  DEFAULT_TIMESTR_FORMAT,
  getLocaleDateStringUTC,
  timeToMinutes,
  formatTimeRangeWithDayspan,
  addLabelToTimeRange,
  formatTimeRange,
  DEFAULT_HOUR_FORMAT,
  findMinMaxDate,
  dateCompare,
  addTimeToDateUTC,
  addDaysToDateUTC,
  DateGroup,
} from './time';
import { PropertyReservablesConfig, PropertyZone } from '../types/property';
import { PartialBy } from '../types/typeUtils';

export interface ReservablePipelineEvents {
  eventTemplates: ReservableEventTemplate[];
  collections: {
    segments: BrandSegment[];
    levels: BrandLevel[];
    attributes: BrandAttribute[];
    zones: PropertyZone[];
  };
}

export const DEFAULT_HOST_KEY = '_host';
export const DEFAULT_RENTAL_KEY = '_rental';

export type ReservePaymentType = 'Charge' | 'Fee' | 'Transfer' | 'Refund';
export type ReservePaymentMethod =
  | 'Stripe'
  | 'StripeTerminal'
  | 'Cash'
  | 'Miscellaneous'
  | 'PlatformFee'
  | 'PlatformReversal'
  | 'Tax'
  | 'TaxReversal'
  | 'PropertyTransfer'
  | 'PropertyReversal'
  | 'HostTransfer'
  | 'HostReversal';

export function createDefaultTicket(): ReservableTicket {
  return {
    _id: new ObjectId().toHexString(),
    name: 'Adult',
    description: '',
    price: 10000,
    images: [],
    fields: [
      {
        _id: new ObjectId().toHexString(),
        label: 'Full Name',
        type: 'input',
        settings: {
          type: 'text',
          options: [],
          constraints: {
            min: 0,
            max: 0,
            step: 0,
          },
        },
        required: true,
      },
    ],
  };
}

export function createDefaultPackage(): ReservablePackage {
  return {
    _id: new ObjectId().toHexString(),
    name: 'Package',
    description: '',
    price: 10000,
    pricePerTicket: false,
    images: [],
  };
}

export function createDefaultAddon(): ReservableAddon {
  return {
    _id: new ObjectId().toHexString(),
    name: 'Addon',
    description: '',
    price: 1000,
    pricePerTicket: false,
    images: [],
  };
}

export function createDefaultCustomer(): ReservationCustomer {
  return {
    firstName: 'Default',
    lastName: 'Customer',
    email: 'example@gmail.com',
    phone: '305-000-0000',
  };
}

export function createDefaultRule(): BareReserveScheduleRule {
  return {
    _id: new ObjectId().toHexString(),
    ruleFragments: createDefaultRuleFragments(),
    slots: [],
    host: undefined,
    hidden: false,
  };
}

export function createDefaultRuleFragments(): ReserveScheduleRuleParts {
  return {
    start: getServerDateLocal().iso,
    until: getServerDateLocal().iso,
    isDynamicSetPos: false,
    frequency: 'NEVER',
    byMonth: 1,
    byMonthDay: 1,
    bySetPos: 1,
    byDay: 'MO',
    endType: 'NEVER',
    count: 1,
    interval: 1,
  };
}

export function createStripeMetadata(data: {
  propertyId: string;
  pendingPaymentId?: string;
  reservationDraftIds?: string[];
  reservationDrafts?: Array<{
    id: string;
    reservationCode?: string;
  }>;
  reservation?: {
    id: string;
    code?: string;
  };
  cartSession?: {
    id: string;
    code?: string;
  };
  taxAmountCents?: number;
  taxCalculationId?: string;
}): Record<string, string> {
  const obj: Record<string, string> = {
    propertyId: forceIdToString(data.propertyId),
  };

  if (data.pendingPaymentId) {
    obj.pendingPaymentId = forceIdToString(data.pendingPaymentId);
  }

  if (data.reservation) {
    obj.reservationId = forceIdToString(data.reservation.id);
    if (data.reservation.code) {
      obj.reservationCode = forceIdToString(data.reservation.code);
    }
  }

  if (data.cartSession) {
    obj.cartSessionId = forceIdToString(data.cartSession.id);
    if (data.cartSession.code) {
      obj.cartSessionCode = forceIdToString(data.cartSession.code);
    }
  }

  if (data.reservationDraftIds) {
    obj.reservationDraftIds = data.reservationDraftIds.map(forceIdToString).join(',');
  }

  if (data.reservationDrafts && data.reservationDrafts.length > 0) {
    obj.reservationCodes = data.reservationDrafts
      .map(d => d.reservationCode)
      .filter(Boolean)
      .join(',');
  }

  obj.taxAmountCents = String(data.taxAmountCents || 0);

  if (data.taxCalculationId) {
    obj.taxCalculationId = data.taxCalculationId;
  }

  return obj;
}

export function experienceIsRental(
  experienceType: RESERVABLE_EXPERIENCE_TYPE | undefined
): boolean {
  return experienceType === 'rental';
}

export function slotsAreSimilar(slot1: ReserveCacheSlot, slot2: ReserveCacheSlot): boolean {
  return (
    slot1.time.hour === slot2.time.hour &&
    slot1.time.minute === slot2.time.minute &&
    slot1.duration === slot2.duration &&
    slot1.rental === slot2.rental &&
    slot1.host === slot2.host
  );
}

export function slotKeyWithEvent(event: string, slotKey: string) {
  return `${event}|${slotKey}`;
}

export function slotKeyFromReservation(reservation: Reservation): string {
  return buildReserveSlotKey(
    {
      date: dateToIsoUTC(reservation.date),
      rental: reservation.rental,
      host: reservation.host,
    },
    reservation.time,
    reservation.duration
  );
}

export function slotKeyFromReservationDraft(draft: ReservationDraft): string {
  return buildReserveSlotKey(
    {
      date: dateToIsoUTC(draft.date),
      rental: draft.rental,
      host: draft.host,
    },
    draft.time,
    draft.duration
  );
}

export function slotKeyFromReserveTimelineItem(
  item: Pick<ReserveTimelineItem, 'rental' | 'host' | 'time' | 'duration'>
): string {
  return buildRentalSlotKeyPart(
    {
      rental: item.rental,
      host: item.host,
    },
    item.time,
    item.duration
  );
}

export function buildReserveSlotKey(
  opts: {
    date: string;
    rental: string | undefined;
    host: string | undefined;
  },
  startTime: Time,
  duration: number | undefined
): string {
  return (
    `${opts.date}|` +
    buildRentalSlotKeyPart({ rental: opts.rental, host: opts.host }, startTime, duration)
  );
}

export function buildRentalSlotKeyPart(
  opts: { rental: string | undefined; host: string | undefined },
  startTime: Time | undefined,
  duration: number | undefined
): string {
  return (
    `${opts.rental || DEFAULT_RENTAL_KEY}|` +
    buildHostTimeSlotKeyPart(opts.host, startTime, duration)
  );
}

export function buildHostTimeSlotKeyPart(
  host: string | undefined,
  startTime: Time | undefined,
  duration: number | undefined
): string {
  return `${host || DEFAULT_HOST_KEY}|` + buildTimeSlotKeyPart(startTime, duration);
}

export function buildTimeSlotKeyPart(
  startTime: Time | undefined,
  duration: number | undefined
): string {
  return `${startTime?.hour ?? '??'}:${startTime?.minute ?? '??'}|${duration || 0}`;
}

/**
 * Extract rental from a slotKey
 * SlotKey format: date|rental|host|hour:minute|duration
 * @param slotKey - The slot key to parse
 * @returns The rental ID or DEFAULT_RENTAL_KEY if not specified
 */
export function getRentalFromSlotKey(slotKey: string): string {
  const parts = slotKey.split('|');
  return parts[1] || DEFAULT_RENTAL_KEY;
}

/**
 * Check if two slot keys represent the same rental
 * @param slotKey1 - First slot key
 * @param slotKey2 - Second slot key
 * @returns true if both slots are for the same rental
 */
export function isSameRental(slotKey1: string, slotKey2: string): boolean {
  return getRentalFromSlotKey(slotKey1) === getRentalFromSlotKey(slotKey2);
}

export function bare(schedule: ReserveSchedule): BareReserveSchedule {
  return {
    ...schedule,
    eventTemplate: schedule.eventTemplate._id,
    rules: schedule.rules.map(rule => ({
      ...rule,
      host: rule.host?._id,
    })),
  };
}

export function buildReserveTimeslotLabel(opts: {
  label?: string;
  time?: Time;
  timeDisplay?: string;
  duration?: number;
  rental?: string;
  host?: string;
  dayspan?: number;
}): string {
  const timeRange = opts.time
    ? formatTimeRangeWithDayspan(opts.time, opts.timeDisplay, opts.duration, opts.dayspan)
    : '';
  const timeLabel = addLabelToTimeRange(timeRange, opts.label);

  const base = [opts.rental, timeLabel].filter(notFalsy).join(' • ');

  if (!opts.host) return base;

  return base ? `${base} • by ${opts.host}` : `by ${opts.host}`;
}

export type DaySpannableSlot = Omit<ReserveScheduleSlot, 'limits'> &
  ReserveScheduleSlotDayContext & {
    dayspanCapacity: number;
    spotsLeft: number;
  };

export function getDayspanSubsequentSlots<T extends DaySpannableSlot, U extends DaySpannableSlot>(
  timeslots: T[],
  slot: U
): T[] {
  const dayspan = slot.dayspan;
  if (dayspan <= 1) return [];

  const slotKeySuffix = buildRentalSlotKeyPart(
    { rental: slot.rental, host: slot.host },
    slot.time,
    slot.duration
  );

  const subsequent = timeslots
    .filter(
      ts =>
        ts.slotKey !== slot.slotKey &&
        safeIdCompare(ts.ruleId, slot.ruleId) &&
        buildRentalSlotKeyPart({ rental: ts.rental, host: ts.host }, ts.time, ts.duration) ===
          slotKeySuffix &&
        new Date(ts.date).getTime() > new Date(slot.date).getTime()
    )
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(0, dayspan - 1);

  return subsequent;
}

// self-sufficient alternative to getDayspanSubsequentSlots/getDayspanSubsequentSlotsFromIndex:
// derives the dayspan-1 subsequent-day slots directly from the given slot's own date + dayspan,
// rather than looking for matching entries elsewhere in a timeslot list. That list is often
// cache-derived, and a rental's later days can have already condensed into a pooled slot (see
// rental realization), which silently drops the match and leaves additionalOccurrences incomplete.
export function buildDayspanSubsequentSlots<T extends DaySpannableSlot>(slot: T): T[] {
  const dayspan = slot.dayspan;
  if (dayspan <= 1) return [];

  return Array.from({ length: dayspan - 1 }, (_, i) => ({
    ...slot,
    date: addDaysToDateUTC(slot.date, i + 1),
  }));
}

type DayspanSlotIndex<T> = Map<string, { time: number; item: T }[]>;

function dayspanGroupKey(
  slot: Pick<DaySpannableSlot, 'ruleId' | 'rental' | 'host' | 'time' | 'duration'>
): string {
  return (
    forceIdToString(slot.ruleId) +
    '|' +
    buildRentalSlotKeyPart({ rental: slot.rental, host: slot.host }, slot.time, slot.duration)
  );
}

// Groups timeslots by their recurring-slot identity (ruleId + rental/host/time/duration)
// and sorts each group by date ascending. Built once so subsequent-day lookups become a
// binary search per slot instead of a full re-scan of every timeslot (avoids O(N^2)).
export function buildDayspanSlotIndex<T extends DaySpannableSlot>(
  timeslots: T[]
): DayspanSlotIndex<T> {
  const index: DayspanSlotIndex<T> = new Map();

  for (const item of timeslots) {
    const key = dayspanGroupKey(item);
    let group = index.get(key);
    if (!group) {
      group = [];
      index.set(key, group);
    }
    group.push({ time: new Date(item.date).getTime(), item });
  }

  for (const group of index.values()) {
    group.sort((a, b) => a.time - b.time);
  }

  return index;
}

// Index-backed equivalent of getDayspanSubsequentSlots: the next `dayspan - 1` slots of the
// same recurring definition that fall on a strictly later date. Requires an index built from
// the same timeslot set; produces the same result as getDayspanSubsequentSlots.
// meant to be a performance improvement over repeatedly scanning the full timeslot list
export function getDayspanSubsequentSlotsFromIndex<
  T extends DaySpannableSlot,
  U extends DaySpannableSlot
>(index: DayspanSlotIndex<T>, slot: U): T[] {
  const dayspan = slot.dayspan;
  if (dayspan <= 1) return [];

  const group = index.get(dayspanGroupKey(slot));
  if (!group) return [];

  const slotTime = new Date(slot.date).getTime();

  // upper bound: first entry with a strictly later date
  let lo = 0;
  let hi = group.length;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (group[mid].time > slotTime) hi = mid;
    else lo = mid + 1;
  }

  return group.slice(lo, lo + dayspan - 1).map(entry => entry.item);
}

// this is not real capacity or spots left. this considers the dayspan capacity
// essentially means that the slot would normally be available but it is not because
// it cannot guarantee that availabiliy across the dayspan
// used mostly for display purposes to indicate that a slot is not available due to dayspan capacity
// rather than actual slot capacity
interface SlotSpaceStatus {
  spotsLeft: number;
  capacity: number;
  dynamicCapacity: number;
  dynamicSpotsLeft: number;
}

export function getSlotSpaceStatus<T extends DaySpannableSlot>(item: T): SlotSpaceStatus {
  const hasTakenSlots = item.capacity !== item.spotsLeft;

  const dynamicCapacity = hasTakenSlots
    ? item.capacity
    : Math.min(item.dayspanCapacity, item.capacity);
  const dynamicSpotsLeft = Math.min(item.spotsLeft, dynamicCapacity);

  return {
    capacity: Math.max(item.capacity, 0),
    spotsLeft: Math.max(item.spotsLeft, 0),
    dynamicCapacity: Math.max(dynamicCapacity, 0),
    dynamicSpotsLeft: Math.max(dynamicSpotsLeft, 0),
  };
}

type SlotSpaceStatusWithLimits = SlotSpaceStatus & {
  _raw: {
    capacity: number;
    spotsLeft: number;
  };
  _limits: {
    capacity: number;
    spotsLeft: number;
  };
  _dynamic: {
    capacity: number;
    spotsLeft: number;
  };
};

export function getSlotSpaceStatusWithLimits<
  T extends DaySpannableSlot & Pick<ReserveCacheSlot, 'limits'>
>(item: T, ticketId?: string): SlotSpaceStatusWithLimits {
  const limitCapacity = item.limits?.[ticketId || '']?.capacity ?? Number.MAX_SAFE_INTEGER;
  const limitSpotsLeft = item.limits?.[ticketId || '']?.spotsLeft ?? Number.MAX_SAFE_INTEGER;

  const { dynamicCapacity, dynamicSpotsLeft } = getSlotSpaceStatus(item);

  return {
    capacity: Math.max(Math.min(item.capacity, limitCapacity), 0),
    spotsLeft: Math.max(Math.min(item.spotsLeft, limitSpotsLeft), 0),
    dynamicCapacity: Math.max(Math.min(dynamicCapacity, limitCapacity), 0),
    dynamicSpotsLeft: Math.max(Math.min(dynamicSpotsLeft, limitSpotsLeft), 0),
    _raw: {
      capacity: item.capacity,
      spotsLeft: item.spotsLeft,
    },
    _limits: {
      capacity: limitCapacity,
      spotsLeft: limitSpotsLeft,
    },
    _dynamic: {
      capacity: dynamicCapacity,
      spotsLeft: dynamicSpotsLeft,
    },
  };
}

export function expandRuleDates(
  schedules: BareReserveSchedule[],
  startDate: string,
  endDate: string
): Map<string, Set<string>> {
  const startObj = isoToDateUTC(startDate);
  const endObj = isoToDateUTC(endDate);
  const result = new Map<string, Set<string>>();

  for (const schedule of schedules) {
    for (const rule of schedule.rules) {
      const key = rule._id.toString();
      if (result.has(key)) continue;
      const rrule = rruleFromScheduleRule(rule.ruleFragments);
      const dates = rrule.between(startObj, endObj, true);
      result.set(key, new Set(dates.map(d => dateToIsoUTC(d))));
    }
  }

  return result;
}

export function getScheduleRuleFromDate(
  rules: BareReserveScheduleRule[],
  dateIso: string,
  host?: string | null, // null means skip this check, undefined means treat as no host
  rental?: string | null, // null means skip this check, undefined means treat as no rental
  preExpanded?: Map<string, Set<string>>
) {
  for (const rule of rules) {
    const rentals = rule.rentals ?? [];

    // rule must not be hidden
    if (rule.hidden) continue;

    // rule must apply to date
    const inRule = preExpanded
      ? preExpanded.get(rule._id.toString())?.has(dateIso) ?? false
      : rruleCheckDate(rruleFromScheduleRule(rule.ruleFragments), dateIso);
    if (!inRule) continue;

    if (host !== null) {
      // if host is not specified, rule must not have a host (must be host agnostic)
      if (!host && rule.host) continue;

      // if host is specified, rule must match the host (cannot be host agnostic)
      if (host && (!rule.host || !safeIdCompare(rule.host, host))) continue;
    }

    if (rental) {
      // rule must match the rental (can be rental agnostic or include the rental)
      if (rentals.length > 0 && !rentals.some(ruleRental => safeIdCompare(ruleRental, rental))) {
        continue;
      }
    }

    return rule;
  }
}

export function getReservableEventsFromSchedules(
  inputDates: string[],
  reserveSchedules: BareReserveSchedule[]
) {
  const reserveEventsByDate: ReserveEventsByDate = {};

  inputDates.forEach(dateIso => {
    reserveSchedules.forEach(schedule => {
      const relevantRule = getScheduleRuleFromDate(schedule.rules, dateIso, null, null);

      relevantRule?.communications?.forEach(comm => {
        if (!reserveEventsByDate[dateIso]) {
          reserveEventsByDate[dateIso] = [];
        }
        const reserveEvent: ReserveEvent = {
          _id: comm._id,
          template: forceIdToString(schedule.eventTemplate),
          venue: forceIdToString(comm.venue),
          startTime: comm.time,
          duration: comm.duration,
          staffAllocations: [],
          hidden: false,
          day: '0',
          callToActions: {
            syncWithTemplate: true,
            values: [],
          },
        };
        reserveEventsByDate[dateIso].push(reserveEvent);
      });
    });
  });

  return reserveEventsByDate;
}

export function getReservationDraftProgress(draft: ReservationDraft): CheckoutProgress {
  const hasDateTime = !!(draft.date && draft.time);

  // Check if tickets were selected (quantity > 0)
  const hasTickets = !!(draft.tickets && draft.tickets.length > 0);

  // Check if all tickets have all required fields filled
  const hasTicketDetails = !!(
    draft.tickets &&
    draft.tickets.length > 0 &&
    draft.tickets.every(ticketSelection => {
      // Check if values object exists
      if (!ticketSelection.values) return false;

      // Check if all required fields from the ticket definition are filled
      const requiredFields = ticketSelection.ticket.fields?.filter(field => field.required) || [];

      return requiredFields.every(field => {
        const fieldKey = keyFromLabel(field.label);

        return (
          ticketSelection.values.length > 0 &&
          ticketSelection.values.every(
            v => v[fieldKey] !== undefined && v[fieldKey] !== null && v[fieldKey] !== ''
          )
        );
      });
    })
  );

  // Check if all required reservation-level fields are filled
  const requiredReservationFields =
    draft.eventSnapshot?.reservables?.fields?.filter(f => f.required) ?? [];
  const hasReservationDetails = requiredReservationFields.every(field => {
    const fieldKey = keyFromLabel(field.label);
    const val = draft.values?.[fieldKey];
    return val !== undefined && val !== null && val !== '';
  });

  const hasAddons =
    (draft.addons ?? []).reduce((sum, addonSelection) => sum + (addonSelection.quantity || 0), 0) >
    0;
  const requiresAddon = !!draft.eventSnapshot?.reservables?.requireAtLeastOneAddon;
  const hasRequiredAddons = !requiresAddon || hasAddons;
  const hasPackages = !!(draft.packages && draft.packages.length > 0);
  const canProceedToCheckout =
    hasDateTime && hasTickets && hasTicketDetails && hasReservationDetails && hasRequiredAddons;

  return {
    dateTime: {
      completed: hasDateTime,
      optional: false,
    },
    tickets: {
      completed: hasTickets,
      optional: false,
    },
    ticketDetails: {
      completed: hasTicketDetails,
      optional: false,
    },
    reservationDetails: {
      completed: hasReservationDetails,
      optional: false,
    },
    packages: {
      completed: hasPackages,
      optional: true,
    },
    addons: {
      completed: hasRequiredAddons,
      optional: true,
    },
    checkout: {
      completed: false, // Will be true after payment
      optional: false,
    },
    canProceedToCheckout,
  };
}

export function getReservationRevenue(reservation: Reservation | FlattenedReservation): number {
  const total = reservation.payments.reduce(
    (sum, p) =>
      sum +
      (p.type === 'Charge'
        ? p.amount
        : p.type === 'Refund'
        ? -p.amount
        : p.method === 'PlatformFee'
        ? -p.amount
        : p.method === 'Tax'
        ? -p.amount
        : p.method === 'TaxReversal'
        ? p.amount
        : 0),
    0
  );

  if (isFlattenedReservation(reservation)) {
    const chunks = Math.floor(total / reservation._totalOccurrences);
    const thisChunk = !reservation._isLastOccurrence
      ? chunks
      : total - chunks * (reservation._totalOccurrences - 1);
    return thisChunk;
  }

  return total;
}

export function summarizeReservationPayments(reservation: Reservation): ReservationPaymentSummary {
  const payments = reservation.payments ?? [];
  let charges = 0;
  let refunds = 0;
  let tax = 0;
  let platformFee = 0;
  let propertyTransfer = 0;
  let hostTransfer = 0;
  let firstPaymentDate: string | null = null;
  let lastPaymentDate: string | null = null;

  for (const p of payments) {
    const date = dateToIsoUTC(p.date);
    if (!firstPaymentDate || date < firstPaymentDate) firstPaymentDate = date;
    if (!lastPaymentDate || date > lastPaymentDate) lastPaymentDate = date;

    if (p.type === 'Charge') charges += p.amount;
    else if (p.type === 'Refund') refunds += p.amount;
    else if (p.method === 'Tax') tax += p.amount;
    else if (p.method === 'TaxReversal') tax -= p.amount;
    else if (p.method === 'PlatformFee') platformFee += p.amount;
    else if (p.method === 'PlatformReversal') platformFee -= p.amount;
    else if (p.method === 'PropertyTransfer') propertyTransfer += p.amount;
    else if (p.method === 'PropertyReversal') propertyTransfer -= p.amount;
    else if (p.method === 'HostTransfer') hostTransfer += p.amount;
    else if (p.method === 'HostReversal') hostTransfer -= p.amount;
  }

  const revenue = getReservationRevenue(reservation);
  return {
    revenue,
    charges,
    refunds,
    tax,
    platformFee,
    propertyTransfer,
    hostTransfer,
    firstPaymentDate,
    lastPaymentDate,
  };
}

export function addGlyphsToPayment(
  price: string,
  type: ReservePaymentType,
  method: ReservePaymentMethod
): string {
  return type === 'Refund'
    ? `-${price}`
    : type === 'Charge'
    ? `+${price}`
    : method === 'HostTransfer' || method === 'PropertyTransfer'
    ? `(${price})`
    : method === 'HostReversal' || method === 'PropertyReversal'
    ? `(${price})`
    : type === 'Fee'
    ? ['TaxReversal', 'PlatformReversal'].includes(method)
      ? `+${price}`
      : `-${price}`
    : `${price}`;
}

export function getReservationAvailableFundsForRefund(
  reservation?: Reservation
): [ReservationPayment | undefined, number] {
  if (!reservation) return [undefined, 0];

  const stripeCharges = reservation.payments.filter(
    p => p.method === 'Stripe' && p.type === 'Charge' && !!p.groupId
  );

  const refundedByChargeId = reservation.payments
    .filter(p => p.method === 'Stripe' && p.type === 'Refund' && !!p.groupId)
    .reduce((acc, payment) => {
      acc[payment.groupId] = (acc[payment.groupId] || 0) + payment.amount;
      return acc;
    }, {} as Record<string, number>);

  const availableStripeCharge = [...stripeCharges].reverse().find(charge => {
    const grossAmount = charge.amount;
    const alreadyRefundedAmount = refundedByChargeId[charge.groupId] || 0;
    const refundableAmount = grossAmount - alreadyRefundedAmount;

    return refundableAmount > 0;
  });

  const maxRefundAmount = availableStripeCharge
    ? Math.max(
        0,
        availableStripeCharge.amount - (refundedByChargeId[availableStripeCharge.groupId] || 0)
      )
    : 0;

  return [availableStripeCharge, maxRefundAmount];
}

export interface PricingLineItem {
  id: string;
  price: number;
  name: string;
  label: string;
  description: string;
  quantity: number;
  type: 'Ticket' | 'Package' | 'Addon' | 'Fee';
  taxCode?: string;
}

interface PricingBuildingBlocks {
  eventSnapshot?: ReservableEventTemplateSnapshot;
  priceOverrides?: ReserveScheduleSlotPriceOverrides;
  tickets?: Array<{ quantity: number; ticket: { _id: string; price: number; name: string } }>;
  packages?: Array<{
    quantity: number;
    package: Pick<ReservablePackage, '_id' | 'price' | 'name' | 'pricePerTicket' | 'taxCode'>;
  }>;
  addons?: Array<{
    quantity: number;
    addon: Pick<ReservableAddon, '_id' | 'price' | 'name' | 'pricePerTicket' | 'taxCode'>;
  }>;
}

export function getTicketPriceAfterOverrides<T extends { _id: string; price: number }>(
  ticket: T,
  priceOverrides?: ReserveScheduleSlotPriceOverrides
): number {
  return priceOverrides?.[ticket._id] ?? priceOverrides?.all ?? ticket.price;
}

export function calculatePricingHelper(records: PricingBuildingBlocks[]) {
  let ticketTotal = 0;
  let packageTotal = 0;
  let addonTotal = 0;
  let feeTotal = 0;

  let ticketLineItems: PricingLineItem[] = [];
  let packageLineItems: PricingLineItem[] = [];
  let addonLineItems: PricingLineItem[] = [];
  let feeLineItems: PricingLineItem[] = [];
  let summaryLineItems: PricingLineItem[] = [];

  for (const rec of records) {
    let thisRecordTicketTotal = 0;
    let thisRecordPackageTotal = 0;
    let thisRecordAddonTotal = 0;
    let thisRecordFeeTotal = 0;

    const eventTemplate = rec.eventSnapshot;
    const totalTickets = (rec.tickets ?? []).reduce((sum, t) => sum + t.quantity, 0);

    for (const ticketSelection of rec.tickets ?? []) {
      const ticketBasePrice = getTicketPriceAfterOverrides(
        ticketSelection.ticket,
        rec.priceOverrides
      );
      const ticketSumPrice = ticketBasePrice * ticketSelection.quantity;

      const item: PricingLineItem = {
        id: ticketSelection.ticket._id,
        type: 'Ticket',
        price: ticketBasePrice,
        name: ticketSelection.ticket.name,
        label: `Ticket: ${ticketSelection.ticket.name}`,
        description: eventTemplate?.title ?? 'Experience Ticket',
        quantity: ticketSelection.quantity,
        taxCode: eventTemplate?.reservables.taxCode,
      };

      ticketLineItems.push(item);
      summaryLineItems.push(item);

      thisRecordTicketTotal += ticketSumPrice;
    }

    // Calculate from packages
    for (const packageSelection of rec.packages ?? []) {
      const packageBasePrice =
        packageSelection.package.price *
        (packageSelection.package.pricePerTicket ? totalTickets : 1);
      const packageSumPrice = packageBasePrice * packageSelection.quantity;

      const item: PricingLineItem = {
        id: packageSelection.package._id,
        type: 'Package',
        price: packageBasePrice,
        name: packageSelection.package.name,
        label: `Package: ${packageSelection.package.name}`,
        description: eventTemplate?.title ?? 'Experience Package',
        quantity: packageSelection.quantity,
        // taxCode: packageSelection.package.taxCode,
        taxCode: eventTemplate?.reservables.taxCode,
      };

      packageLineItems.push(item);
      summaryLineItems.push(item);

      thisRecordPackageTotal += packageSumPrice;
    }

    // Calculate from addons
    for (const addonSelection of rec.addons ?? []) {
      const addonBasePrice =
        addonSelection.addon.price * (addonSelection.addon.pricePerTicket ? totalTickets : 1);
      const addonSumPrice = addonBasePrice * addonSelection.quantity;

      const item: PricingLineItem = {
        id: addonSelection.addon._id,
        type: 'Addon',
        price: addonBasePrice,
        name: addonSelection.addon.name,
        label: `Addon: ${addonSelection.addon.name}`,
        description: eventTemplate?.title ?? 'Experience Addon',
        quantity: addonSelection.quantity,
        // taxCode: addonSelection.addon.taxCode,
        taxCode: eventTemplate?.reservables.taxCode,
      };

      addonLineItems.push(item);
      summaryLineItems.push(item);

      thisRecordAddonTotal += addonSumPrice;
    }

    // Calculate gratuity fees
    const gratuityFeePercentage = eventTemplate?.reservables?.gratuityFeePercentage ?? 0;
    const gratuityFeeFlatAmountCents = eventTemplate?.reservables?.gratuityFeeFlatAmountCents ?? 0;

    if (gratuityFeePercentage > 0 || gratuityFeeFlatAmountCents > 0) {
      const gratuitiesBasePrice = Math.ceil(
        (gratuityFeePercentage / 100) *
          (thisRecordTicketTotal + thisRecordPackageTotal + thisRecordAddonTotal) +
          gratuityFeeFlatAmountCents
      );
      const gratuitiesSumPrice = gratuitiesBasePrice; // quantity is always 1 for fees

      const item: PricingLineItem = {
        id: 'gratuity-fee',
        type: 'Fee',
        price: gratuitiesSumPrice,
        name: 'Gratuity Fee',
        label: 'Gratuity Fee',
        description: eventTemplate?.title ?? 'Experience Addon',
        quantity: 1,
        taxCode: eventTemplate?.reservables?.taxCode,
      };

      feeLineItems.push(item);
      summaryLineItems.push(item);

      thisRecordFeeTotal += gratuitiesSumPrice;
    }

    ticketTotal += thisRecordTicketTotal;
    packageTotal += thisRecordPackageTotal;
    addonTotal += thisRecordAddonTotal;
    feeTotal += thisRecordFeeTotal;
  }

  const finalTotal = ticketTotal + packageTotal + addonTotal + feeTotal;

  return {
    ticketTotal,
    packageTotal,
    addonTotal,
    feeTotal,
    finalTotal,
    ticketLineItems,
    packageLineItems,
    addonLineItems,
    feeLineItems,
    summaryLineItems,
  };
}

export function calculateReservationDraftPricing(drafts: ReservationDraft[]) {
  return calculatePricingHelper(
    drafts.map(d => ({
      tickets: d.tickets,
      packages: d.packages,
      addons: d.addons,
      eventSnapshot: d.eventSnapshot,
      priceOverrides: d.slotSnapshot?.priceOverrides,
    }))
  );
}

export function calculateReservationPricing(reservations: Reservation[]) {
  const pricingRecords = reservations.map(r => {
    const { eventSnapshot } = r;

    const ticketOpts = eventSnapshot.reservables.tickets;
    const ticketOptsDict = mapBy(ticketOpts, t => t._id);

    const packageOpts = eventSnapshot.reservables.packages;
    const packageOptsDict = mapBy(packageOpts, p => p._id);

    const addonOpts = eventSnapshot.reservables.addons;
    const addonOptsDict = mapBy(addonOpts, a => a._id);

    const ticketGroups = groupBy(r.tickets, t => t.ticketId);

    const mappedItem: PricingBuildingBlocks = {
      eventSnapshot,
      priceOverrides: r.priceOverrides,
      tickets: Object.keys(ticketGroups).map(ticketId => ({
        quantity: ticketGroups[ticketId].length,
        ticket: {
          _id: ticketId,
          price: ticketOptsDict[ticketId]?.price || 0,
          name: ticketOptsDict[ticketId]?.name || 'Unknown Ticket',
        },
      })),
      packages: r.packages.map(p => ({
        quantity: p.quantity,
        package: {
          _id: p.packageId,
          price: packageOptsDict[p.packageId]?.price || 0,
          name: packageOptsDict[p.packageId]?.name || 'Unknown Package',
          pricePerTicket: packageOptsDict[p.packageId]?.pricePerTicket || false,
        },
      })),
      addons: r.addons.map(a => ({
        quantity: a.quantity,
        addon: {
          _id: a.addonId,
          price: addonOptsDict[a.addonId]?.price || 0,
          name: addonOptsDict[a.addonId]?.name || 'Unknown Addon',
          pricePerTicket: addonOptsDict[a.addonId]?.pricePerTicket || false,
        },
      })),
    };

    return mappedItem;
  });

  return calculatePricingHelper(pricingRecords);
}

export function calculateServiceFeeLineItem(
  lineItems: PricingLineItem[],
  propResConfig: PropertyReservablesConfig | undefined
) {
  const serviceFeeFlatAmountCents = propResConfig?.serviceFeeFlatAmountCents ?? 0;
  const serviceFeePercentage = propResConfig?.serviceFeePercentage ?? 0;

  const finalTotal = lineItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  let serviceFeeItem: PricingLineItem | undefined;

  if (serviceFeeFlatAmountCents > 0 || serviceFeePercentage > 0) {
    const serviceFeeCents = Math.ceil(
      (serviceFeePercentage / 100) * finalTotal + serviceFeeFlatAmountCents
    );
    serviceFeeItem = {
      id: 'service-fee',
      price: serviceFeeCents,
      name: 'Service Fee',
      label: 'Service Fee',
      description: '',
      quantity: 1,
      type: 'Fee',
    };
  }

  return serviceFeeItem;
}

export function cartSessionCanCheckout(
  drafts: ReservationDraft[],
  cartSession: CartSession
): boolean {
  const draftsOk =
    drafts.map(d => getReservationDraftProgress(d)).findIndex(p => !p.canProceedToCheckout) < 0;

  const cartOk =
    !!cartSession.customer &&
    !!cartSession.customer.email &&
    !!cartSession.customer.phone &&
    !!cartSession.customer.firstName &&
    !!cartSession.customer.lastName;

  return draftsOk && cartOk;
}

export function createReservationCode(): string {
  const base = Date.now().toString();
  const salt = Math.floor(Math.random() * 1e6).toString();
  const hash = crypto
    .createHash('sha256')
    .update(base + salt)
    .digest('base64')
    .replace(/[^A-Z0-9]/gi, '')
    .substring(0, 8)
    .toUpperCase();
  return `RZ-${hash}`;
}

export function createReservationCartCode(): string {
  const base = Date.now().toString();
  const salt = Math.floor(Math.random() * 1e6).toString();
  const hash = crypto
    .createHash('sha256')
    .update(base + salt)
    .digest('base64')
    .replace(/[^A-Z0-9]/gi, '')
    .substring(0, 8)
    .toUpperCase();
  return `RC-${hash}`;
}

export function calculateReservationAnalytics(
  reservations: Array<Reservation | FlattenedReservation>
): ReservationAnalytics {
  let totalRevenue = 0;

  let numOfTickets = 0;
  let numOfPackages = 0;
  let numOfAddons = 0;

  let ticketTally: { [key: string]: Array<{ name: string; customer: string; quantity: number }> } =
    {};
  let packageTally: { [key: string]: Array<{ name: string; customer: string; quantity: number }> } =
    {};
  let addonTally: { [key: string]: Array<{ name: string; customer: string; quantity: number }> } =
    {};

  reservations.forEach(reservation => {
    totalRevenue += getReservationRevenue(reservation);

    if (reservation.status === 'cancelled') return;

    const customer = makeFullName(reservation.customer.firstName, reservation.customer.lastName);
    const pricing = calculateReservationPricing([reservation]);

    numOfTickets += reservation.tickets.length;
    numOfPackages += reservation.packages.reduce((sum, p) => sum + p.quantity, 0);
    numOfAddons += reservation.addons.reduce((sum, a) => sum + a.quantity, 0);

    pricing.ticketLineItems.forEach(({ id, name, quantity }) => {
      if (!ticketTally[id]) ticketTally[id] = [];
      ticketTally[id].push({
        name,
        quantity,
        customer,
      });
    });

    pricing.packageLineItems.forEach(({ id, name, quantity }) => {
      if (!packageTally[id]) packageTally[id] = [];
      packageTally[id].push({
        name,
        quantity,
        customer,
      });
    });

    pricing.addonLineItems.forEach(({ id, name, quantity }) => {
      if (!addonTally[id]) addonTally[id] = [];
      addonTally[id].push({
        name,
        quantity,
        customer,
      });
    });
  });

  const tally: ReservationAnalytics['tally'] = {
    tickets: Object.entries(ticketTally).map(([id, lines]) => ({
      id,
      name: lines[0]?.name || 'Unknown Ticket',
      lines,
      total: lines.reduce((sum, l) => sum + l.quantity, 0),
    })),
    packages: Object.entries(packageTally).map(([id, lines]) => ({
      id,
      name: lines[0]?.name || 'Unknown Package',
      lines,
      total: lines.reduce((sum, l) => sum + l.quantity, 0),
    })),
    addons: Object.entries(addonTally).map(([id, lines]) => ({
      id,
      name: lines[0]?.name || 'Unknown Addon',
      lines,
      total: lines.reduce((sum, l) => sum + l.quantity, 0),
    })),
  };

  let popularity: ReservationAnalytics['popularity'] = {
    popularTicket: tally.tickets.find(
      t => t.total === Math.max(...tally.tickets.map(tt => tt.total))
    ),
    popularPackage: tally.packages.find(
      p => p.total === Math.max(...tally.packages.map(pp => pp.total))
    ),
    popularAddon: tally.addons.find(a => a.total === Math.max(...tally.addons.map(aa => aa.total))),
  };

  return {
    totalRevenue,
    numOfTickets,
    numOfPackages,
    numOfAddons,
    tally,
    popularity,
  };
}

export function isReservableEvent(
  eventTemplate?: BareEventTemplate | null
): eventTemplate is ReservableEventTemplate {
  return !eventTemplate?.archived && (eventTemplate?.reservables?.tickets ?? []).length > 0;
}

function idList(items: { _id: string }[] = []): string {
  return items
    .map(i => i._id)
    .sort()
    .join(',');
}

export function reservablesCacheAffectingFieldsChanged(
  oldTemplate: Pick<BareEventTemplate, 'reservables'> | null | undefined,
  newTemplate: Pick<BareEventTemplate, 'reservables'>
): boolean {
  const oldR = oldTemplate?.reservables;
  const newR = newTemplate.reservables;
  if (!oldR || !newR) return true;

  return (
    oldR.bookingWindowDays !== newR.bookingWindowDays ||
    oldR.bookingOpenDays !== newR.bookingOpenDays ||
    oldR.experienceType !== newR.experienceType ||
    idList(oldR.tickets) !== idList(newR.tickets)
    // idList(oldR.packages) !== idList(newR.packages) ||
    // idList(oldR.addons) !== idList(newR.addons)
  );
}

export function isReservablePopulatedEvent(eventTemplate?: EventTemplate | null) {
  return !eventTemplate?.archived && (eventTemplate?.reservables?.tickets ?? []).length > 0;
}

export function buildCalendarMarkers<T extends { time: Time; duration?: number; host?: string }>(
  dateIso: string,
  timeslotsByDate: GroupedItems<ReserveCacheSlot>,
  ticketQuantities?: Record<string, number>,
  specificTimeOption?: T,
  specificRental?: string
): string[] {
  let hasExactOption = false;
  let hasOtherOptions = false;

  function checkAvailable(slot: ReserveCacheSlot, slotSpaceStatus: SlotSpaceStatusWithLimits) {
    let check = false;

    if (
      specificTimeOption &&
      buildHostTimeSlotKeyPart(
        specificTimeOption.host,
        specificTimeOption.time,
        specificTimeOption.duration
      ) !== buildHostTimeSlotKeyPart(slot.host, slot.time, slot.duration)
    ) {
      return check;
    }

    // Check specific rental
    if (specificRental && slot.rental !== specificRental) {
      return check;
    }

    check =
      slotSpaceStatus.dynamicSpotsLeft > 0 &&
      Object.keys(ticketQuantities || {}).every(
        ticketId => !checkTicketsSelectedAreAvailable(ticketId, slot, ticketQuantities || {})
      );

    return check;
  }

  (timeslotsByDate[dateIso] ?? []).forEach(slot => {
    const slotSpaceStatus = getSlotSpaceStatusWithLimits(slot);

    hasExactOption ||= checkAvailable(slot, slotSpaceStatus);
    hasOtherOptions = slotSpaceStatus._dynamic.spotsLeft > 0;
  });

  // Return markers for the specific date
  return hasExactOption ? ['#22c55e'] : hasOtherOptions ? ['#facc15'] : ['#ef4444'];
}

export function checkTicketsSelectedAreAvailable(
  ticketId: string,
  selectedSlot: ReserveCacheSlot,
  ticketQuantities: Record<string, number>,
  ticketReservationLimits?: Record<string, number>
): string | undefined {
  const thisTicketQty = ticketQuantities[ticketId] ?? 0;
  const allTicketsQty = Object.values(ticketQuantities).reduce((sum, v) => sum + v, 0);
  const hasQty = thisTicketQty > 0;

  const reservationLimit = ticketReservationLimits?.[ticketId];
  if (hasQty && reservationLimit !== undefined && thisTicketQty > reservationLimit) {
    return 'Exceeds the limit per reservation';
  }

  const spaceStatus = getSlotSpaceStatusWithLimits(selectedSlot);
  const ticketSpaceStatus = getSlotSpaceStatusWithLimits(selectedSlot, ticketId);

  const individualCheck = thisTicketQty > ticketSpaceStatus.dynamicSpotsLeft;
  const sumCheck = allTicketsQty > spaceStatus.dynamicSpotsLeft;

  return hasQty && (individualCheck || sumCheck) ? 'Not enough availability' : undefined;
}

export function snapshotFromTimeslot(timeslot: ReserveCacheSlot): ReserveScheduleSlotSnapshot {
  return {
    slotKey: timeslot.slotKey,
    priceOverrides: timeslot.priceOverrides,
  };
}

export function snapshotFromEventTemplate(
  event: ReservableEventTemplate
): ReservableEventTemplateSnapshot {
  return {
    _id: new ObjectId().toHexString(),
    eventTemplate: event._id,
    title: event.title,
    description: event.description,
    audience: event.audience,
    reservables: {
      tickets: event.reservables.tickets,
      packages: event.reservables.packages,
      addons: event.reservables.addons,
      requireAtLeastOneAddon: !!event.reservables.requireAtLeastOneAddon,
      meetingPoint: event.reservables.meetingPoint,
      gratuityFeeFlatAmountCents: event.reservables.gratuityFeeFlatAmountCents,
      gratuityFeePercentage: event.reservables.gratuityFeePercentage,
      hostPercentage: event.reservables.hostPercentage,
      experienceType: event.reservables.experienceType,
      fields: event.reservables.fields,
      taxCode: event.reservables.taxCode,
      disclaimer: event.reservables.disclaimer,
      instructions: event.reservables.instructions,
      externalId: event.reservables.externalId,
      autoAssignRental: event.reservables.autoAssignRental,
    },
  };
}

export function isReservationReleased(reservation: Reservation, currentDateIso: string): boolean {
  return (
    !!reservation.spotReleased &&
    [
      dateToIsoUTC(reservation.date),
      ...(reservation.additionalOccurrences ?? []).map(o => dateToIsoUTC(o.date)),
    ].some(dateIso => dateIso >= currentDateIso)
  );
}

// Given a set of weighted, half-open [start, end) usage intervals, returns the maximum sum of
// weights active at any single instant. Equivalent to, for every interval u, summing the weight
// of every interval whose range contains u.start and taking the overall max - but computed with
// a sort + sweep instead of comparing every pair, since the max can only increase at a start
// event (an end event can only remove weight, never introduce a new maximum).
function maxConcurrentUsage(usages: { start: number; end: number; seatsTaken: number }[]): number {
  if (usages.length === 0) return 0;

  const events: { pos: number; delta: number }[] = [];
  usages.forEach(u => {
    events.push({ pos: u.start, delta: u.seatsTaken });
    events.push({ pos: u.end, delta: -u.seatsTaken });
  });

  // at equal positions, process removals before additions so a usage ending exactly when
  // another starts doesn't count as overlapping (matches the `other.end > u.start` semantics)
  events.sort((a, b) => a.pos - b.pos || a.delta - b.delta);

  let running = 0;
  let max = 0;
  events.forEach(e => {
    running += e.delta;
    if (running > max) max = running;
  });

  return max;
}

export function checkReservationErrors(props: {
  experienceType: RESERVABLE_EXPERIENCE_TYPE;
  tickets: Array<Pick<ReservableTicket, '_id'>>;
  rental?: string;
  // occurrences[0] is treated as the reservation's primary occurrence (as opposed to one of its
  // additionalOccurrences) - it anchors the wrongDayspan check, which only makes sense against
  // the occurrence the reservation was originally booked against
  occurrences: Array<PartialBy<ReservationOccurrence, '_id' | 'time'>>;
  serverDate: DateGroup;
  // slots this exact reservation already legitimately holds - skipped by willOverbookIt (that's
  // normal occupancy, not a conflict), but never skipped by isOverbooked, which reports a slot
  // that's over capacity regardless of who's occupying it
  savedSlotKeys?: Set<string>;
  // omit while the caller's slot data hasn't loaded yet - isOverbooked/willOverbookIt/
  // timeslotMissing/wrongDayspan all stay false rather than reporting false positives against
  // no data
  getSlotStatus?: (
    slotKey: string
  ) => Pick<ReserveCacheSlot, 'spotsLeft' | 'overcapacity' | 'dayspan'> | undefined;
}) {
  const { experienceType, tickets, rental, occurrences, serverDate, savedSlotKeys, getSlotStatus } =
    props;

  const isRental = experienceIsRental(experienceType);

  const upcomingOccurrences = occurrences.filter(occ => dateToIsoUTC(occ.date) >= serverDate.iso);

  const mismatchingTimes =
    new Set(
      occurrences
        .filter(occ => !!occ.time)
        .map(occ => formatTimeRange(occ.time!, occ.duration, occ.timeDisplay))
    ).size > 1;

  const mismatchingHostsOrRentals = occurrences.some(
    occ =>
      !safeIdCompare(occ.host, upcomingOccurrences[0]?.host) ||
      !safeIdCompare(occ.rental, upcomingOccurrences[0]?.rental)
  );

  let isOverbooked = false;
  let willOverbookIt = false;
  let timeslotMissing = false;
  let wrongDayspan = false;
  let expectedDayspan: number | undefined;
  let mainStatus: Pick<ReserveCacheSlot, 'spotsLeft' | 'overcapacity' | 'dayspan'> | undefined;

  if (getSlotStatus) {
    upcomingOccurrences.forEach(occ => {
      if (!occ.time) return;
      const slotKey = buildReserveSlotKey(
        { date: dateToIsoUTC(occ.date), rental: occ.rental, host: occ.host },
        occ.time,
        occ.duration
      );
      const status = getSlotStatus(slotKey);

      if (!status) {
        timeslotMissing = true;
        return;
      }

      // a slot already over capacity is a genuine conflict regardless of whether this
      // reservation is one of the occupants
      if (status.overcapacity) isOverbooked = true;

      // a slot this reservation doesn't already hold that doesn't have enough room for its
      // own demand - separate from isOverbooked, since a slot with plenty of room can still
      // not fit this reservation's ticket count
      if (!savedSlotKeys?.has(slotKey) && status.spotsLeft < tickets.length) willOverbookIt = true;
    });

    const mainOccurrence = occurrences[0];
    if (mainOccurrence?.time) {
      const mainSlotKey = buildReserveSlotKey(
        {
          date: dateToIsoUTC(mainOccurrence.date),
          rental: mainOccurrence.rental,
          host: mainOccurrence.host,
        },
        mainOccurrence.time,
        mainOccurrence.duration
      );
      mainStatus = getSlotStatus(mainSlotKey);
      if (mainStatus) {
        expectedDayspan = mainStatus.dayspan;
        wrongDayspan = mainStatus.dayspan !== occurrences.length;
      }
    }
  }

  // pooled rental slots behave like ticket-type slots (no specific unit assigned, quantities
  // allowed) even though the experience itself is a rental type - see rentalPoolingActive - so a
  // rental-type reservation landing on a pooled date legitimately has no single assigned unit.
  // Without slot data to know whether the main occurrence is pooled, this falls back to the
  // stricter (non-pooled) check.
  const missingRental =
    isRental && (tickets.length > 1 || !tickets[0] || !safeIdCompare(tickets[0]._id, rental));

  const errors = {
    missingRental: {
      active: missingRental,
      message: 'Tickets requested do not match the selected rental.',
    },
    mismatchingTimes: {
      active: mismatchingTimes,
      message: `Some dates have different time labels across occurrences.`,
    },
    mismatchingHostsOrRentals: {
      active: mismatchingHostsOrRentals,
      message: 'Some dates have different rentals or hosts.',
    },
    isOverbooked: {
      active: isOverbooked,
      message: 'Some dates are already booked beyond capacity.',
    },
    willOverbookIt: {
      active: willOverbookIt,
      message: "Some dates don't have enough room for this reservation.",
    },
    timeslotMissing: {
      active: timeslotMissing,
      message: 'Some dates do not match any available timeslot.',
    },
    wrongDayspan: {
      active: wrongDayspan,
      message: "This reservation's occurrence count doesn't match its slot's expected dayspan.",
      expectedDayspan,
    },
  };

  const hasErrors = Object.values(errors).some(error => error.active);

  return { hasErrors, errors };
}

export function buildReserveTimeline(
  from: string,
  to: string,
  schedules: BareReserveSchedule[],
  eventTemplates: BareEventTemplate[],
  reservations: Reservation[],
  bypassDayChecks?: boolean,
  includeCancelled?: boolean,
  includeReleased?: boolean
): ReserveTimeline[] {
  const serverDate = getServerDateLocal();
  const eventTemplateDict = mapBy(eventTemplates, t => t._id);

  const timelines: ReserveTimeline[] = [];
  const dates = createIsoDateList(from, daysBetweenIsoDates(from, to) + 1);
  const preExpanded = expandRuleDates(schedules, from, to);

  // sort each rule's slots once up front instead of re-sorting the same array on every
  // date/host/rental combination that matches the rule (rule.slots doesn't depend on any of those)
  schedules.forEach(schedule => {
    schedule.rules.forEach(rule => rule.slots.sort(reserveTimeSorter));
  });

  // fill reservations into timeline
  const reservationsByDate = groupBy(flattenReservations(reservations), r => dateToIsoUTC(r.date));
  const reservationFilter = (r: Reservation) =>
    (includeCancelled || r.status !== 'cancelled') &&
    (includeReleased || !isReservationReleased(r, serverDate.iso));

  dates.forEach(date => {
    const timelineEvents: ReserveTimelineEvent[] = [];

    schedules.forEach(schedule => {
      const template = eventTemplateDict[schedule.eventTemplate];
      if (!isReservableEvent(template)) return;

      if (!bypassDayChecks) {
        const { bookingOpenDays, bookingWindowDays } = template.reservables;

        // if booking window days is provided, then the current date must be
        // at least that many days in the future from today
        if (bookingWindowDays !== undefined) {
          const daysUntil = daysBetweenIsoDates(serverDate.iso, date);
          if (daysUntil < bookingWindowDays) return;
        }

        // if booking open days is provided, then the current date must be within
        // that many days from the event date
        if (bookingOpenDays !== undefined) {
          const daysFrom = daysBetweenIsoDates(serverDate.iso, date);
          if (daysFrom > bookingOpenDays) return;
        }
      }

      const timelineItems: ReserveTimelineItem[] = [];
      const isRental = experienceIsRental(template.reservables?.experienceType);

      // we must consider all host and the non-hosted rules separately
      const hosts = [DEFAULT_HOST_KEY, ...schedule.rules.map(r => r.host)]
        .filter(notUndefined)
        .filter(onlyUnique)
        .map(forceIdToString);

      // we consider all rentals if it's a rental-type experience, otherwise
      // we just use a single "default" rental which skips the rental check
      const rentals = (
        isRental ? template.reservables.tickets.map(t => t._id) : [DEFAULT_RENTAL_KEY]
      )
        .filter(notUndefined)
        .filter(onlyUnique)
        .map(forceIdToString);

      hosts.forEach(host => {
        rentals.forEach(rental => {
          const rule = getScheduleRuleFromDate(
            schedule.rules,
            date,
            host === DEFAULT_HOST_KEY ? undefined : host,
            rental === DEFAULT_RENTAL_KEY ? undefined : rental,
            preExpanded
          );

          if (rule) {
            rule.slots.forEach(slot => {
              // this handles date exceptions
              const exceptionCheck = slot.exceptions?.some(
                ex => ex.date === date && (!isRental || safeIdCompare(ex.rental, rental))
              );

              timelineItems.push({
                ...slot,
                _id: new ObjectId().toHexString(),
                slotId: slot._id,
                ruleId: rule._id,
                dayspan: slot.dayspan || 1, // safety
                slotKey: buildReserveSlotKey(
                  {
                    date,
                    rental: rental === DEFAULT_RENTAL_KEY ? undefined : rental,
                    host,
                  },
                  slot.time,
                  slot.duration
                ),
                date: isoToDateUTC(date),
                host: host === DEFAULT_HOST_KEY ? undefined : host,
                rental: rental === DEFAULT_RENTAL_KEY ? undefined : rental,
                capacity: exceptionCheck ? 0 : isRental ? 1 : slot.capacity,
                // will be filled in later
                overlaps: [],
                spotsLeft: 0,
                seatsTaken: 0,
                directSeatsTaken: 0,
                indirectSeatsTaken: 0,
                directReservations: [],
                indirectReservations: [],
                dayspanCapacity: 0,
                // ---
                isFloatingSlot: false,
              });
            });
          }
        });
      });

      if (timelineItems.length > 0) {
        timelineEvents.push({
          template,
          items: timelineItems,
        });
      }
    });

    const timeline: ReserveTimeline = {
      date,
      events: timelineEvents,
    };

    timelines.push(timeline);

    // index events by template id and items by slotKey so reservation folding below is O(1)
    // per reservation instead of a linear scan of events/items (kept in sync as entries are added)
    const eventByTemplateKey = new Map<string, ReserveTimelineEvent>();
    const itemsBySlotKeyByEvent = new Map<ReserveTimelineEvent, Map<string, ReserveTimelineItem>>();
    timeline.events.forEach(e => {
      const key = String(e.template._id);
      // first-match-wins, matching the .find() semantics this replaces
      if (!eventByTemplateKey.has(key)) eventByTemplateKey.set(key, e);
      const itemMap = new Map<string, ReserveTimelineItem>();
      e.items.forEach(i => itemMap.set(i.slotKey, i));
      itemsBySlotKeyByEvent.set(e, itemMap);
    });

    // fill reservations into timeline
    (reservationsByDate[timeline.date] || []).forEach(res => {
      if (!reservationFilter(res)) return;

      const templateKey = String(res.eventSnapshot.eventTemplate);
      let event = eventByTemplateKey.get(templateKey);
      const template = eventTemplateDict[res.eventSnapshot.eventTemplate];

      if (!event && template) {
        event = {
          template: eventTemplateDict[res.eventSnapshot.eventTemplate],
          items: [],
        };
        timeline.events.push(event);
        eventByTemplateKey.set(templateKey, event);
        itemsBySlotKeyByEvent.set(event, new Map());
      }

      const slotKey = slotKeyFromReservation(res);
      const itemMap = event ? itemsBySlotKeyByEvent.get(event) : undefined;

      let item = itemMap?.get(slotKey);

      if (!item) {
        // this is a dangling reservation - slot that wasn't created in the step above because
        // it doesn't match the schedule rules, but we still want to show the reservation in the
        // timeline so we create a "fake" slot for it
        item = {
          _id: new ObjectId().toHexString(),
          slotId: new ObjectId().toHexString(),
          ruleId: new ObjectId().toHexString(),
          slotKey,
          date: isoToDateUTC(date),
          host: res.host,
          rental: res.rental,
          time: res.time,
          duration: res.duration,
          // will be filled in later
          capacity: 0,
          overlaps: [],
          spotsLeft: 0,
          seatsTaken: 0,
          directSeatsTaken: 0,
          indirectSeatsTaken: 0,
          directReservations: [],
          indirectReservations: [],
          dayspanCapacity: 0,
          dayspan: 1,
          // ---
          isFloatingSlot: true,
        };

        event?.items.push(item);
        itemMap?.set(slotKey, item);
      }

      item?.directReservations.push(res);
    });

    // calculate overlaps once real *and* floating items both exist for the date - a dangling
    // (floating) reservation occupies real time against a real rental/host just like a direct
    // booking does, so it needs to be in the same overlap graph, not excluded from it
    timeline.events.forEach(event => {
      const groups = groupBy(event.items, item => `${item.host ?? ''}|${item.rental ?? ''}`);
      Object.values(groups).forEach(group => {
        for (let i = 0; i < group.length; i++) {
          for (let j = i + 1; j < group.length; j++) {
            if (
              doTimeRangesOverlap(
                group[i].time,
                group[i].duration,
                group[j].time,
                group[j].duration
              )
            ) {
              group[i].overlaps.push(group[j].slotKey);
              group[j].overlaps.push(group[i].slotKey);
            }
          }
        }
      });
    });

    // fill occupancy into timeline
    const allDayItems = timeline.events.flatMap(e => e.items);
    const itemBySlotKey = mapBy(allDayItems, 'slotKey');

    for (const item of allDayItems) {
      item.directSeatsTaken = item.directReservations.reduce((sum, r) => sum + r.tickets.length, 0);

      const indirectReservations: FlattenedReservation[] = [];
      const overlappingUsages: { start: number; end: number; seatsTaken: number }[] = [];

      item.overlaps.forEach(skey => {
        const overlappingSlot = itemBySlotKey[skey];
        if (!overlappingSlot) return;

        const thisIndirectReservations = overlappingSlot.directReservations;
        indirectReservations.push(...thisIndirectReservations);

        const thisIndirectSeatsTaken = thisIndirectReservations.reduce(
          (sum, r) => sum + r.tickets.length,
          0
        );
        if (thisIndirectSeatsTaken > 0) {
          const start = timeToMinutes(overlappingSlot.time);
          overlappingUsages.push({
            start,
            end: start + (overlappingSlot.duration ?? 0),
            seatsTaken: thisIndirectSeatsTaken,
          });
        }
      });

      // the max seats that could be occupied at any single instant, since non-overlapping
      // indirect reservations (e.g. two indirect bookings that don't overlap each other)
      // don't stack on top of one another - used for capacity/spotsLeft math only
      // always less than indirect seats taken
      const concurrentIndirectSeatsTaken = maxConcurrentUsage(overlappingUsages);

      item.seatsTaken = item.directSeatsTaken + concurrentIndirectSeatsTaken;
      item.indirectReservations = indirectReservations;
      item.indirectSeatsTaken = indirectReservations.reduce((sum, r) => sum + r.tickets.length, 0);
      item.spotsLeft = Math.max(item.capacity - item.seatsTaken, 0);
    }
  });

  const allTimelineItems = timelines.flatMap(t => t.events.flatMap(e => e.items));
  const dayspanIndex = buildDayspanSlotIndex(allTimelineItems);

  allTimelineItems.forEach(item => {
    const dayspan = item.dayspan;
    if (dayspan <= 1) {
      item.dayspanCapacity = item.spotsLeft;
      return;
    }

    const spanSlots = getDayspanSubsequentSlotsFromIndex(dayspanIndex, item);
    if (spanSlots.length < dayspan - 1) {
      item.dayspanCapacity = 0;
      item.dayspanCapacityIsInaccurate = true;
      return;
    }

    const normalizedSpotsLeft = Math.max(Math.min(...spanSlots.map(s => s.spotsLeft)), 0);
    item.dayspanCapacity = normalizedSpotsLeft;
  });

  return timelines;
}

export function makeReservationFromOccurrence(
  reservation: Reservation,
  occurrence: ReservationOccurrence
): FlattenedReservation {
  const { additionalOccurrences, ...rest } = reservation;
  const thisOccurrenceIndex =
    additionalOccurrences?.findIndex(o => safeIdCompare(o._id, occurrence._id)) ?? -1;

  const [from, to] = findMinMaxDate(
    reservation.date,
    ...(additionalOccurrences ?? []).map(o => o.date)
  );

  return {
    ...rest,
    ...occurrence,
    _isOccurrenceOf: reservation._id,
    _occurrenceIndex: thisOccurrenceIndex + 1,
    additionalOccurrences: [],
    _totalOccurrences: (additionalOccurrences ?? []).length + 1,
    _isLastOccurrence:
      thisOccurrenceIndex === -1 ||
      thisOccurrenceIndex === (additionalOccurrences?.length ?? 0) - 1,
    _firstDate: dateToIsoUTC(from),
    _lastDate: dateToIsoUTC(to),
  };
}

export function flattenReservations(reservations: Reservation[]): FlattenedReservation[] {
  const flattened: FlattenedReservation[] = [];

  reservations.forEach(reservation => {
    const { additionalOccurrences, ...rest } = reservation;
    const occurrencesCount = additionalOccurrences?.length ?? 0;

    const [from, to] = findMinMaxDate(
      reservation.date,
      ...(additionalOccurrences ?? []).map(o => o.date)
    );

    flattened.push({
      ...rest,
      _isOccurrenceOf: null,
      _occurrenceIndex: 0,
      additionalOccurrences: [],
      _totalOccurrences: 1 + occurrencesCount,
      _isLastOccurrence: occurrencesCount === 0,
      _firstDate: dateToIsoUTC(from),
      _lastDate: dateToIsoUTC(to),
    });

    (additionalOccurrences ?? []).forEach(occurrence => {
      flattened.push(makeReservationFromOccurrence(reservation, occurrence));
    });
  });

  return flattened;
}

export function isFlattenedReservation(
  reservation: Reservation | FlattenedReservation
): reservation is FlattenedReservation {
  return (reservation as FlattenedReservation)._isOccurrenceOf !== undefined;
}

export function getEnhancedReservationStatus(
  reservation: Reservation | FlattenedReservation
): EnhancedReservationStatus {
  const currentDate = new Date();
  const [firstDate, lastDate] = findMinMaxDate(
    reservation.date,
    ...(reservation.additionalOccurrences ?? []).map(o => o.date),
    isFlattenedReservation(reservation) && reservation._firstDate
      ? isoToDateUTC(reservation._firstDate)
      : reservation.date,
    isFlattenedReservation(reservation) && reservation._lastDate
      ? isoToDateUTC(reservation._lastDate)
      : reservation.date
  );

  const firstDateWithTime = addTimeToDateUTC(firstDate || reservation.date, reservation.time);
  const lastDateWithTime = addTimeToDateUTC(lastDate || reservation.date, reservation.time);

  if (reservation.status === 'cancelled' && reservation.lastCancelledDate) {
    return dateCompare(reservation.lastCancelledDate, firstDateWithTime) < 0
      ? 'cancelled'
      : 'lateCancelled';
  }

  if (reservation.status === 'active') {
    return dateCompare(currentDate, lastDateWithTime) > 0
      ? 'complete'
      : dateCompare(currentDate, firstDateWithTime) > 0
      ? 'inProgress'
      : 'active';
  }

  return reservation.status;
}

export function getEnhancedStatusVariant(res: Reservation | FlattenedReservation) {
  const status = getEnhancedReservationStatus(res);

  switch (status) {
    case 'cancelled':
      return 'crimson';
    case 'lateCancelled':
      return 'grape';
    case 'complete':
      return 'lime';
    case 'inProgress':
      return 'banana';
    case 'active':
      return 'ocean';
    default:
      return 'slate';
  }
}

export function buildTimeOption(
  slot: ReserveCacheSlot,
  hosts?: MappedItems<BareEventHost>
): TimeOption {
  const { dynamicCapacity, dynamicSpotsLeft } = getSlotSpaceStatus(slot);

  return {
    key: buildHostTimeSlotKeyPart(slot.host, slot.time, slot.duration),
    time: slot.time,
    duration: slot.duration,
    host: slot.host,
    timeLabel: formatTimeRangeWithDayspan(slot.time, slot.timeDisplay, slot.duration, slot.dayspan),
    hostLabel: slot.host
      ? makeFullName(hosts?.[slot.host]?.firstName ?? '', hosts?.[slot.host]?.lastName ?? '')
      : undefined,
    dayspan: slot.dayspan,
    spotsLeft: dynamicSpotsLeft,
    capacity: dynamicCapacity,
    label: slot.label,
  };
}

export function buildTicketSelections(
  tickets: ReservableTicket[],
  draftTickets?: { ticket: { _id: string }; quantity: number }[]
): TicketSelections {
  if (draftTickets) {
    return {
      quantities: draftTickets.reduce((acc, t) => {
        acc[t.ticket._id] = t.quantity;
        return acc;
      }, {} as Record<string, number>),
    };
  }

  return {
    quantities: tickets.reduce((acc, t) => {
      acc[t._id] = 0;
      return acc;
    }, {} as Record<string, number>),
  };
}

export function buildDateOption(
  isoDate: string,
  timeslots?: GroupedItems<ReserveCacheSlot>
): DateOption {
  const date = isoToDateLocal(isoDate);

  const timeOnlyKeys = (timeslots?.[isoDate] ?? [])
    .map(s => buildHostTimeSlotKeyPart(s.host, s.time, s.duration))
    .filter(onlyUnique);

  return {
    key: isoDate,
    date,
    weekday: getLocaleDateStringLocal(date, { weekday: 'short' }),
    monthDay: getLocaleDateStringLocal(date, { month: 'short', day: 'numeric' }),
    timesCount: timeOnlyKeys.length,
  };
}

export function getTimeOptions(timeslots: ReserveCacheSlot[], hosts?: MappedItems<BareEventHost>) {
  const timeOptionsMap: Record<string, TimeOption> = {};

  for (const slot of timeslots) {
    const timeOption = buildTimeOption(slot, hosts);

    if (!timeOptionsMap[timeOption.key]) {
      timeOptionsMap[timeOption.key] = timeOption;
    } else {
      const objToModify = timeOptionsMap[timeOption.key];
      objToModify.spotsLeft += timeOption.spotsLeft;
      objToModify.capacity += timeOption.capacity;
      // if any rental merged into this option is multi-day, treat the whole option as
      // multi-day for grouping purposes - understating it (hiding that a dayspan booking is
      // involved) is worse than overstating it
      objToModify.dayspan = Math.max(objToModify.dayspan, timeOption.dayspan);

      if (timeOption.label !== objToModify.label && !objToModify.label?.endsWith(' (& more)')) {
        objToModify.label = `${objToModify.label} (& more)`;
      }
    }
  }

  const options = Object.values(timeOptionsMap).sort(reserveTimeSorter);

  return { timeOptionsMap, options };
}

export function reserveTimeSorter<
  T extends { time: Time; dayspan: number; duration?: number; hostLabel?: string }
>(a: T, b: T) {
  // grouped by exact dayspan first, ascending: all 1-day slots, then all 2-day, then all
  // 3-day, etc. - this is the same grouping used for cache-derived timeslots, so any UI mixing
  // schedule slots and cache slots stays consistent
  const dayspanDiff = a.dayspan - b.dayspan;
  if (dayspanDiff !== 0) {
    return dayspanDiff;
  }

  // then by time, then by duration, then by host
  const timeDiff = timeCompare(a.time, b.time);
  if (timeDiff !== 0) {
    return timeDiff;
    // longest duration first, with undefined durations (for open-ended slots) treated as 0
  } else if ((a.duration ?? 0) !== (b.duration ?? 0)) {
    return (b.duration ?? 0) - (a.duration ?? 0);
  } else {
    return (a.hostLabel ?? '').localeCompare(b.hostLabel ?? '');
  }
}

// One flagged item per (type, experience, date, rental, host) - not per detection. Every place
// that finds a problem looks this key up first: a new key creates the item, an existing key just
// appends the newly-found reservation(s) (deduped by _id) to the one that's already there. This
// is what makes "the same problem, found from two different angles" (an overlapping slot seen
// from both sides, or two different reservations independently breaking the same slot) collapse
// into one row instead of two, without a separate post-processing pass.
function addNeedsAttentionOccupants(
  itemsByKey: Map<string, NeedsAttentionItem>,
  opts: {
    type: NeedsAttentionType;
    templateId: string;
    date: string;
    event: string;
    rental?: string;
    host?: string;
    capacity: number;
    reservations: FlattenedReservation[];
  }
) {
  const key = `${opts.type}|${opts.templateId}|${opts.date}|${opts.rental ?? ''}|${
    opts.host ?? ''
  }`;
  const existing = itemsByKey.get(key);

  if (!existing) {
    itemsByKey.set(key, {
      type: opts.type,
      date: opts.date,
      event: opts.event,
      capacity: opts.capacity,
      reservations: [...opts.reservations],
    });
    return;
  }

  const seenIds = new Set(existing.reservations.map(r => r._id));
  opts.reservations.forEach(r => {
    if (seenIds.has(r._id)) return;
    seenIds.add(r._id);
    existing.reservations.push(r);
  });
}

export function buildNeedsAttentionItems(
  timelines: ReserveTimeline[],
  // the authoritative reservation list (and their event templates) - NOT derived from timelines,
  // since timelines are date-windowed (see findFlagged) and a reservation whose main occurrence
  // has already passed would otherwise silently lose visibility into its own past occurrences,
  // corrupting checks that need the reservation's *complete* occurrence set (missingRental,
  // mismatchingTimes, mismatchingHostsOrRentals, wrongDayspan)
  reservations: Reservation[],
  eventTemplates: BareEventTemplate[],
  // this file is shared code that can run client-side too (see SlotMappingPage.tsx), where
  // getServerDateLocal() would read the browser's clock/timezone instead of the server's -
  // callers must supply the actual server-synced date themselves (server-side: getServerDateLocal()
  // directly; client-side: usePublicServerInfo()'s serverDate, fetched from the server)
  serverDate: DateGroup
): NeedsAttentionItem[] {
  const itemsByKey = new Map<string, NeedsAttentionItem>();
  const slotStatusByKey = new Map<
    string,
    Pick<ReserveCacheSlot, 'spotsLeft' | 'overcapacity' | 'dayspan'>
  >();

  // this walk exists for one reason: overcapacity needs the aggregate occupancy (seatsTaken,
  // overlaps, direct + indirect occupants) that buildReserveTimeline already computed across
  // every reservation touching a slot - that's a cross-reservation question a single reservation
  // can't answer about itself. Reservation-centric checks are handled separately below, straight
  // off the reservations list, using this walk only to build a slotKey -> status lookup table.
  timelines.forEach(timeline => {
    timeline.events.forEach(event => {
      const templateId = forceIdToString(event.template._id);

      event.items.forEach(slot => {
        const { directReservations, directSeatsTaken, seatsTaken, indirectReservations } = slot;

        // only real slots stand in as "this occurrence has a valid, currently-scheduled slot" -
        // a floating slot's key is deliberately left out, matching how isFloatingSlot already
        // signals that directly for the 'unassigned' check below
        if (!slot.isFloatingSlot) {
          slotStatusByKey.set(`${templateId}|${slot.slotKey}`, {
            spotsLeft: slot.spotsLeft,
            overcapacity: seatsTaken > slot.capacity,
            dayspan: slot.dayspan,
          });
        }

        if (directSeatsTaken < 1) return;

        if (slot.isFloatingSlot) {
          addNeedsAttentionOccupants(itemsByKey, {
            type: 'unassigned',
            templateId,
            date: timeline.date,
            event: event.template.title,
            rental: slot.rental,
            host: slot.host,
            capacity: 0,
            reservations: directReservations,
          });
        } else if (seatsTaken > slot.capacity) {
          // this needs everyone contributing to *this* slot's capacity, direct or indirect
          addNeedsAttentionOccupants(itemsByKey, {
            type: 'overcapacity',
            templateId,
            date: timeline.date,
            event: event.template.title,
            rental: slot.rental,
            host: slot.host,
            capacity: slot.capacity,
            reservations: [...directReservations, ...indirectReservations],
          });
        }
      });
    });
  });

  buildSharedCheckNeedsAttentionItems(
    itemsByKey,
    reservations,
    eventTemplates,
    slotStatusByKey,
    serverDate
  );

  const items = [...itemsByKey.values()];
  items.sort((a, b) => a.date.localeCompare(b.date));

  return items;
}

// Runs checkReservationErrors once per reservation, iterating the reservations list directly
// (not derived from the timeline walk above) so each reservation is always checked against its
// own complete occurrence set, regardless of whether part of its span falls before "today".
function buildSharedCheckNeedsAttentionItems(
  itemsByKey: Map<string, NeedsAttentionItem>,
  reservations: Reservation[],
  eventTemplates: BareEventTemplate[],
  slotStatusByKey: Map<string, Pick<ReserveCacheSlot, 'spotsLeft' | 'overcapacity' | 'dayspan'>>,
  serverDate: DateGroup
): void {
  const templatesById = mapBy(eventTemplates, t => t._id);

  reservations.forEach(reservation => {
    const template = templatesById[reservation.eventTemplate];
    if (!isReservableEvent(template)) return;

    const templateId = forceIdToString(reservation.eventTemplate);
    const group = flattenReservations([reservation]);
    const mainRecord = group.find(r => r._isOccurrenceOf === null) ?? group[0];

    const getSlotStatus = (slotKey: string) => slotStatusByKey.get(`${templateId}|${slotKey}`);

    const { errors } = checkReservationErrors({
      experienceType: template.reservables.experienceType,
      tickets: mainRecord.tickets.map(t => ({ _id: t.ticketId })),
      rental: mainRecord.rental,
      occurrences: group.map(r => ({
        date: r.date,
        time: r.time,
        timeDisplay: r.timeDisplay,
        duration: r.duration,
        host: r.host,
        rental: r.rental,
      })),
      serverDate,
      getSlotStatus,
    });

    // one entry per reservation per key - editing any occurrence opens the same reservation
    const base = {
      templateId,
      date: mainRecord._firstDate,
      event: template.title,
      rental: mainRecord.rental,
      host: mainRecord.host,
      reservations: [mainRecord],
    };

    if (errors.missingRental.active) {
      addNeedsAttentionOccupants(itemsByKey, { ...base, type: 'missing-rental', capacity: 0 });
    }
    if (errors.mismatchingTimes.active) {
      addNeedsAttentionOccupants(itemsByKey, { ...base, type: 'mismatching-times', capacity: 0 });
    }
    if (errors.mismatchingHostsOrRentals.active) {
      addNeedsAttentionOccupants(itemsByKey, {
        ...base,
        type: 'mismatching-hosts-or-rentals',
        capacity: 0,
      });
    }
    if (errors.wrongDayspan.active) {
      addNeedsAttentionOccupants(itemsByKey, {
        ...base,
        type: 'wrong-dayspan',
        capacity: errors.wrongDayspan.expectedDayspan ?? 0,
      });
    }
  });
}

export interface OccurrenceSummary {
  dates: string;
  startTimes: string;
  endTimes: string;
  timeStrings: string;
  timeDisplayStrings: string;
  rentals: string;
  hosts: string;
}

export interface ReservationOccurrenceSummaryInput {
  date: Date;
  time?: Time;
  duration?: number;
  host?: string;
  rental?: string;
  timeDisplay?: string;
  _firstDate?: string;
  _lastDate?: string;
  _totalOccurrences?: number;
}

export function summarizeOccurrences(
  occurrences: ReservationOccurrenceSummaryInput[],
  ticketsById: Record<string, { name: string }>,
  usersById: Record<string, { firstName?: string; lastName?: string }>,
  options?: {
    dateFormat?: 'iso' | 'locale';
    hourFormat?: HourFormat;
    stripSpaces?: boolean;
    condenseDates?: boolean;
  }
): OccurrenceSummary {
  const dates: string[] = [];
  const startTimes = new Set<string>();
  const endTimes = new Set<string>();
  const timeStrings = new Set<string>();
  const timeDisplayStrings = new Set<string>();
  const rentals = new Set<string>();
  const hosts = new Set<string>();

  const [from, to] = findMinMaxDate(
    ...occurrences.flatMap(o =>
      [
        o._firstDate ? isoToDateUTC(o._firstDate) : undefined,
        o._lastDate ? isoToDateUTC(o._lastDate) : undefined,
        o.date,
      ].filter(notUndefined)
    )
  );

  let maxTotalOccurrences = occurrences.length;
  const fromStr = options?.dateFormat === 'iso' ? dateToIsoUTC(from) : getLocaleDateStringUTC(from);
  const toStr = options?.dateFormat === 'iso' ? dateToIsoUTC(to) : getLocaleDateStringUTC(to);

  for (const o of occurrences) {
    if (o._totalOccurrences && o._totalOccurrences > maxTotalOccurrences) {
      maxTotalOccurrences = o._totalOccurrences;
    }

    dates.push(
      options?.dateFormat === 'iso' ? dateToIsoUTC(o.date) : getLocaleDateStringUTC(o.date)
    );

    if (o.time) {
      startTimes.add(
        timeToString(o.time, options?.hourFormat ?? DEFAULT_HOUR_FORMAT, DEFAULT_TIMESTR_FORMAT)
      );
      endTimes.add(
        timeToString(
          addMinutesToTime(o.time, o.duration || 0),
          options?.hourFormat ?? DEFAULT_HOUR_FORMAT,
          DEFAULT_TIMESTR_FORMAT
        )
      );
      timeStrings.add(formatTimeRange(o.time, o.duration, o.timeDisplay));
    }
    if (o.timeDisplay) timeDisplayStrings.add(o.timeDisplay);
    const rental = o.rental ? ticketsById[o.rental]?.name : undefined;
    if (rental) rentals.add(rental);
    const host = o.host
      ? makeFullName(usersById[o.host]?.firstName ?? '', usersById[o.host]?.lastName ?? '')
      : undefined;
    if (host) hosts.add(host);
  }

  const datesAll = dates.join(`,${options?.stripSpaces ? '' : ' '}`);

  const datesSummaryToPart = toStr && toStr !== fromStr ? ` - ${toStr}` : '';
  const datesSummaryTotalPart = maxTotalOccurrences > 1 ? ` (${maxTotalOccurrences})` : '';
  const datesSummary = `${fromStr}${datesSummaryToPart}${datesSummaryTotalPart}`;

  return {
    dates: options?.condenseDates ? datesSummary : datesAll,
    startTimes: [...startTimes].join(`,${options?.stripSpaces ? '' : ' '}`),
    timeStrings: [...timeStrings].join(`,${options?.stripSpaces ? '' : ' '}`),
    timeDisplayStrings: [...timeDisplayStrings].join(`,${options?.stripSpaces ? '' : ' '}`),
    endTimes: [...endTimes].join(`,${options?.stripSpaces ? '' : ' '}`),
    rentals: [...rentals].join(`,${options?.stripSpaces ? '' : ' '}`),
    hosts: [...hosts].join(`,${options?.stripSpaces ? '' : ' '}`),
  };
}

export function makeReservationFromReservationDraft(
  draft: ReservationDraft,
  payments: ReservationPayment[],
  customer?: ReservationCustomer,
  cartCode?: string
): Reservation {
  // build tickets array
  const tickets = (draft.tickets ?? [])
    .map(t =>
      Array.from({ length: t.quantity }).map((_, idx) => ({
        _id: new ObjectId().toHexString(),
        ticketId: t.ticket._id,
        values: t.values[idx] ?? {},
      }))
    )
    .flat();

  // build packages array
  const packages = (draft.packages ?? []).map(p => ({
    _id: new ObjectId().toHexString(),
    packageId: p.package._id,
    quantity: p.quantity,
  }));

  // build addons array
  const addons = (draft.addons ?? []).map(a => ({
    _id: new ObjectId().toHexString(),
    addonId: a.addon._id,
    quantity: a.quantity,
  }));

  const newReservation: Reservation = {
    _id: new ObjectId().toHexString(),
    eventTemplate: draft.eventTemplate,
    reservationCode: draft.reservationCode,
    cartCode,
    property: draft.property,
    date: draft.date,
    time: draft.time,
    timeDisplay: draft.timeDisplay,
    duration: draft.duration,
    rental: draft.rental,
    customer: customer ?? createDefaultCustomer(),
    status: 'active',
    eventSnapshot: draft.eventSnapshot,
    priceOverrides: draft.slotSnapshot?.priceOverrides,
    tickets,
    packages,
    addons,
    host: draft.host,
    fields: draft.values ?? {},
    additionalOccurrences: draft.additionalOccurrences ?? [],
    payments,
    pendingPayments: [],
  };

  return newReservation;
}

export function bareReserveTimeline(timeline: ReserveTimeline): BareReserveTimeline {
  return {
    date: timeline.date,
    events: timeline.events.map(e => ({
      template: e.template._id,
      items: e.items,
    })),
  };
}

export function populateReserveTimeline(
  timeline: BareReserveTimeline,
  eventTemplateMap: MappedItems<BareEventTemplate>
): ReserveTimeline {
  return {
    date: timeline.date,
    events: timeline.events.map(e => ({
      template: eventTemplateMap[e.template]!,
      items: e.items,
    })),
  };
}
